<template>
  <span hidden aria-hidden="true"></span>
</template>

<script>
import { getAccessToken } from 'axios-jwt'

const TERMINAL_CLOSE_CODES = [4401, 4403]
const RECONNECT_DELAYS = [3000, 5000, 10000, 20000, 30000]
const SOCKET_TYPE = 'lead-flow-new-leads'

export default {
  name: 'LeadFlowNewLeadsSocket',
  props: {
    reconcile: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      socket: null,
      reconnectTimer: null,
      reconnectAttempt: 0,
      stopped: false,
      status: 'unavailable'
    }
  },
  mounted() {
    this.stopped = false
    this.connect()
  },
  beforeDestroy() {
    this.stop()
  },
  methods: {
    connect() {
      if (this.stopped || this.hasActiveSocket()) return

      const socketUrl = this.createSocketUrl()
      if (!socketUrl || typeof WebSocket === 'undefined') {
        this.setStatus('unavailable')
        return
      }

      this.setStatus('reconnecting')

      let socket = null
      try {
        socket = new WebSocket(socketUrl)
      } catch (_) {
        this.scheduleReconnect('unavailable')
        return
      }

      this.socket = socket
      socket.onopen = () => {
        if (this.socket !== socket || this.stopped) return
        this.reconnectAttempt = 0
        this.setStatus('connected')
      }
      socket.onmessage = event => this.handleMessage(event)
      socket.onerror = () => {}
      socket.onclose = event => {
        if (this.socket !== socket) return
        this.socket = null
        if (this.stopped) return
        if (TERMINAL_CLOSE_CODES.includes(event.code)) {
          this.clearReconnectTimer()
          this.setStatus('unavailable')
          return
        }

        const status = this.reconnectAttempt ? 'unavailable' : 'reconnecting'
        this.scheduleReconnect(status)
      }
    },
    hasActiveSocket() {
      return this.socket
        && [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)
    },
    createSocketUrl() {
      const rawToken = getAccessToken() || ''
      const token = rawToken.replace(/^Bearer\s+/i, '')
      if (!token) return null

      const apiUrl = process.env.VUE_APP_API_URL || ''
      const isLocalHost = ['127.0.0.1', 'localhost'].includes(window.location.hostname)
      const localSocketOrigin = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:8895`
      const configuredOrigin = process.env.VUE_APP_LEAD_FLOW_WS_URL
      const socketOrigin = configuredOrigin
        || (isLocalHost ? localSocketOrigin : apiUrl.replace(/^http/, 'ws').replace(/\/api\/?$/, ''))

      if (!socketOrigin) return null
      return `${socketOrigin.replace(/\/+$/, '')}/ws/leads/task-tracker/new-leads/?token=${encodeURIComponent(token)}`
    },
    handleMessage(event) {
      let message = null
      try {
        message = JSON.parse(event.data)
      } catch (_) {
        return
      }

      if (!this.isValidEnvelope(message)) return

      const payload = {
        data: message.data,
        occurredAt: message.occurred_at
      }
      if (message.event === 'new_lead.created') this.$emit('created', payload)
      if (message.event === 'new_lead.assigned') this.$emit('assigned', payload)
      if (message.event === 'new_leads.count_changed') this.$emit('count-changed', payload)
    },
    isValidEnvelope(message) {
      if (!this.isRecord(message) || message.type !== SOCKET_TYPE) return false
      if (![
        'new_lead.created',
        'new_lead.assigned',
        'new_leads.count_changed'
      ].includes(message.event)) return false
      if (!this.isValidDate(message.occurred_at) || !this.isRecord(message.data)) return false
      if (message.event === 'new_leads.count_changed') {
        return this.isValidCount(message.data.available_count)
      }
      if (!this.isValidId(message.data.id)) return false

      if (message.event === 'new_lead.created') return this.isValidCreatedLead(message.data)
      return this.isValidAssignedLead(message.data)
    },
    isValidCreatedLead(lead) {
      const waitingAt = lead.waiting_since || lead.created_at
      return this.isValidDate(waitingAt)
        && Array.isArray(lead.activities)
        && this.isRecord(lead.party)
        && this.isRecord(lead.travel)
        && this.isRecord(lead.contact)
        && this.isOptionalRecord(lead.country)
        && this.isOptionalRecord(lead.source)
        && typeof lead.can_assign_to_me === 'boolean'
    },
    isValidAssignedLead(lead) {
      return this.isOptionalRecord(lead.manager)
        && this.isOptionalRecord(lead.current_stage)
        && this.isOptionalRecord(lead.tracker_delta)
    },
    isRecord(value) {
      return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
    },
    isOptionalRecord(value) {
      return value === null || typeof value === 'undefined' || this.isRecord(value)
    },
    isValidId(value) {
      const id = Number(value)
      return Number.isInteger(id) && id > 0
    },
    isValidCount(value) {
      const count = Number(value)
      return Number.isInteger(count) && count >= 0
    },
    isValidDate(value) {
      return typeof value === 'string' && !Number.isNaN(new Date(value).getTime())
    },
    scheduleReconnect(status) {
      if (this.stopped || this.reconnectTimer) return
      if (status) this.setStatus(status)

      const delayIndex = Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)
      const delay = RECONNECT_DELAYS[delayIndex]
      this.reconnectAttempt += 1
      this.reconnectTimer = setTimeout(async () => {
        this.reconnectTimer = null
        if (this.stopped) return

        this.setStatus('reconnecting')
        try {
          await this.reconcile()
        } catch (_) {
          this.setStatus('unavailable')
          this.scheduleReconnect()
          return
        }

        if (!this.stopped) this.connect()
      }, delay)
    },
    setStatus(status) {
      if (this.status === status) return
      this.status = status
      this.$emit('status-change', status)
    },
    clearReconnectTimer() {
      if (!this.reconnectTimer) return
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    },
    stop() {
      this.stopped = true
      this.clearReconnectTimer()
      if (this.socket) {
        const socket = this.socket
        this.socket = null
        try {
          socket.close(1000, 'component destroyed')
        } catch (_) {}
      }
      this.setStatus('unavailable')
    }
  }
}
</script>
